import { useEffect, useRef, type ReactNode } from 'react';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { useAppAuth } from '@/lib/useAppAuth';
import { getDeviceId } from '@/lib/deviceId';
import { LD_CLIENT_SIDE_ID } from './config';

/**
 * Multi-kind context: a `user` (the player when signed in, otherwise the
 * device id — never a shared literal, so per-guest targeting and % rollouts
 * work) plus a `device` that always carries the persisted device id.
 *
 * Experiment flags (`exp.*`) MUST configure their percentage rollout to
 * bucket by the **device** kind: the device key is stable across
 * login/logout, so a guest keeps their assigned variant after signing up.
 * Kill switches and other boolean flags are unaffected.
 */
interface AppLdContext {
  kind: 'multi';
  user: { key: string; email?: string; anonymous?: boolean };
  device: { key: string; anonymous: true };
}

const toContext = (user?: { userId?: number | string; email?: string } | null): AppLdContext => {
  const deviceKey = getDeviceId();
  return {
    kind: 'multi',
    user: user?.userId ? { key: String(user.userId), email: user.email } : { key: deviceKey, anonymous: true },
    device: { key: deviceKey, anonymous: true },
  };
};

/** Stable identity of a context — re-identify only when this changes. */
const contextIdentityKey = (context: AppLdContext): string => `${context.user.key}|${context.device.key}`;

/**
 * Client-side LaunchDarkly for kill-switch UX (banners, disabled CTAs, the
 * maintenance screen) and `exp.*` experiment flags (see `lib/experiments/`).
 * Kill-switch UX only — the api guard is authoritative there.
 *
 * Flags are keyed by their raw dotted key (`useCamelCaseFlagKeys: false`) to
 * match the server registry. Without a client id (local dev / E2E) this is a
 * no-op passthrough: `useFlags()` then returns `{}`, every capability reads
 * as enabled (fail open), and every experiment renders its control.
 *
 * Must render inside both the auth provider (to read the player) AND the
 * QueryClientProvider (`useAppAuth` issues a React Query request). The LD client
 * initializes once at mount; `LdIdentitySync` re-identifies on login/logout so
 * the app subtree is never remounted.
 */
export function LaunchDarklyProvider({ children }: { children: ReactNode }) {
  const { user } = useAppAuth();
  if (!LD_CLIENT_SIDE_ID) return <>{children}</>;

  const context = toContext(user);
  return (
    <LDProvider
      clientSideID={LD_CLIENT_SIDE_ID}
      context={context}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      timeout={10000}
    >
      <LdIdentitySync context={context} />
      {children}
    </LDProvider>
  );
}

/** Re-identifies the LD client when the player's context key changes (login/logout). */
function LdIdentitySync({ context }: { context: AppLdContext }) {
  const ldClient = useLDClient();
  const key = contextIdentityKey(context);
  const isInitial = useRef(true);

  useEffect(() => {
    if (!ldClient) return;
    // The initial context is applied by LDProvider at init; only re-identify on change.
    if (isInitial.current) {
      isInitial.current = false;
      return;
    }
    // A failed re-identify must not surface as an unhandled rejection — the
    // client just keeps serving the previous context's flags (fail open).
    void ldClient.identify(context).catch((err) => {
      console.error('[launchdarkly] re-identify failed', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ldClient, key]);

  return null;
}
